/* eslint-disable react-hooks/exhaustive-deps */
import {
    get, includes, isFunction, isObject, isString, keys, merge, random, sample, toString,
} from 'lodash';

import { v4 as uuidv4 } from 'uuid';
import React, {
    useContext, useState, useMemo, useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import AuthorizationContext from './AuthorizationContext';
import NotificationContext from './NotificationContext';
import LoadingContext, { LOADING_AREAS } from './LoadingContext';
import SettingsContext from './SettingsContext';
import CandidatesLinkedinStatusContext from './CandidatesLinkedinStatusContext';
import TrackingContext from './TrackingContext';
import Logger from '../services/logger';
import { convertTextTemplateForSystem } from '../modules/email/email-editor/template-text-plugin/TemplateTextUtils';
import { HOTJAR_TAGS, TRACKING_EVENTS } from '../constants/TrackingConstants';
import { CONTACT_CHANNEL_TYPES } from '../constants/ContactChannelTypes';
import { CONTACT_MODES } from '../constants/CandidateContactModes';
import { UserRoles, TALENT_EXPERT_ROLE, EMPLOYER_ROLE } from '../constants/UserRole';
import ChromeExtensionService from '../services/ChromeExtensionService';
import ContactCandidateHttpService from '../api/ContactCandidateHttpService';
import WorkflowHttpService from '../api/WorkflowHttpService';
import { WorkflowContactCandidateEvent } from '../services/workflowService';
import UserMessageTemplatesHttpService from '../api/UserMessageTemplatesHttpService';
import LINKEDIN_CONNECTION_STATUSES from '../constants/LinkedinConnectionStatuses';
import LinkedinUserInviteLimitContext from './LinkedinUserInviteLimitContext';
import { getTemplateTypeByFlag } from '../modules/email/contact-candidate/contactCandidateUtils';

const LinkedinMessagesSendingContext = React.createContext({
    lastResults: null,
    sendingProgress: null,
    setSendingProgress: () => {},
    sendMessage: () => {},
    setLastResults: () => {},
    queueLength: 0,
    isDialogOpened: null,
    setIsDialogOpened: () => {},
    cleanLiSendingStates: () => {},
    sendingId: null,
});

const messageSendResults = {};
const queue = [];
const callbacks = [];
const queueTimes = [];
let isQueueRunning = false;

export const LinkedinMessagesSendingProvider = LinkedinMessagesSendingContext.Provider;
// eslint-disable-next-line no-promise-executor-return
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
export const LinkedinMessagesSendingApp = ({ children }) => {
    const { user, authToken } = useContext(AuthorizationContext);
    const { showSuccessNotification, showErrorNotification } = useContext(NotificationContext);
    const { startSavingData, stopSavingData } = useContext(LoadingContext);
    const { trackEvent, trackTag } = useContext(TrackingContext);
    const { userWeeklyInvitesLeft, countUserInvitesForWeek } = useContext(LinkedinUserInviteLimitContext);
    const { t } = useTranslation();
    const {
        systemPreferences: {
            linkedinMessageUserImitationDelayFrom = 3000,
            linkedinMessageUserImitationDelayTo = 15000,
        },
    } = useContext(SettingsContext);

    const { candidatesLinkedinStatuses, fetchCandidatesContactData } = useContext(CandidatesLinkedinStatusContext);

    const [sendingProgress, setSendingProgress] = useState();

    const [lastResults, setLastResults] = useState();
    const [isDialogOpened, setIsDialogOpened] = useState();
    const [lastSendingId, setLastSendingId] = useState(0);

    const SENDING_STATUSES = {
        SUCCESS: 'success',
        FAIL: 'fail',
        PENDING: 'pending',
        SENDING: 'sending',
    };

    const LINKEDIN_SENDING_TIMEOUT = 60000;

    const recipientsSeparator = ' :: ';

    const LI_ERROR_TYPES = {
        NOT_EXIST: { head: t('NOT_EXIST', 'Not exists'), message: t('NOT_EXIST_TXT', 'User doesn\'t exist.') },
        WRONG_ADDR: { head: t('WRONG_PROFILE', 'Wrong profile'), message: t('WRONG_PROFILE_TXT', 'Wrong profile URL.') },
        CONN_PENDING: { head: t('ALREADY_PENDING', 'Already pending'), message: t('ALREADY_PENDING_TXT', 'Connection request is already pending. Can\'t send new requests till user permit the first one.') },
        NOT_CONNECTED: { head: t('NOT_CONNECTED', 'Not connected'), message: t('NOT_CONNECTED_TXT', 'Can\'t send ordinary messages until connect request is sent and permited by user.') },
        DOUBLE_CONNECT: { head: t('ALREADY_CONNECTED', 'Already connected'), message: t('ALREADY_CONNECTED_TXT', 'Can\'t send new connection requests. User is already connected') },
        BLOCKED: { head: t('BLOCKED', 'Blocked'), message: t('BLOCKED_TXT', 'Can\'t send request. User does not allow connection.') },
        AUTH_ERR: { head: t('AUTH_ISSUE', 'Authentication issue'), message: t('AUTH_ISSUE_TXT', 'Authentication error occured.') },
        ACCESS_ERR: { head: t('ACCESS_ISSUE', 'Access issue'), message: t('ACCESS_ISSUE_TXT', 'Access denied.') },
        INVITES_LIMIT: { head: t('NO_INVITES_LEFT', 'No invites left'), message: t('NO_INVITES_LEFT_TXT', 'Can\'t send connect request. Your invite requests limit was exceeded for this week.') },
        UNKNOWN_ERR: { head: t('UNKNOWN_ERROR', 'Unknown error'), message: t('UNKNOWN_ERROR_TXT', 'Unknown error occured. Try again later.') },
        SEND_TIMEOUT: { head: t('SEND_TIMEOUT', 'Sending timeout'), message: t('SEND_TIMEOUT_TXT', 'Sending timeout exceeded') },
        MSG_TOO_LONG: { head: t('MSG_TOO_LONG', 'Message size limit'), message: t('MSG_TOO_LONG_TXT', 'Message is too long') },
    };

    const parseError = (error) => {
        const err = merge({}, LI_ERROR_TYPES?.UNKNOWN_ERR);
        if (isString(error)) {
            err.message = error || err?.message;
        }
        if (isObject(error)) {
            if (error?.text) {
                err.message = error?.text || err?.message;
            }
            if (error?.status) {
                err.status = error?.status;
            }
            err.head = error?.head || err?.head;
            err.message = error?.message || err?.message;
        }
        if (err?.head === LI_ERROR_TYPES?.UNKNOWN_ERR?.head) {
            const msg = (err?.message || '').toLowerCase();
            if (msg !== '') {
                if (msg.includes('invalid profile')) {
                    merge(err, LI_ERROR_TYPES?.WRONG_ADDR);
                } else if (msg.includes('token')) {
                    merge(err, LI_ERROR_TYPES?.AUTH_ERR);
                } else if (msg.includes('auth')) {
                    merge(err, LI_ERROR_TYPES?.AUTH_ERR);
                } else if (msg.includes('access')) {
                    merge(err, LI_ERROR_TYPES?.ACCESS_ERR);
                } else if (msg.includes('blocked')) {
                    merge(err, LI_ERROR_TYPES?.BLOCKED);
                } else if (msg.includes('not exist')) {
                    merge(err, LI_ERROR_TYPES?.NOT_EXIST);
                } else if (msg.includes('limit')) {
                    merge(err, LI_ERROR_TYPES?.INVITES_LIMIT);
                } else if (msg.includes('not connected')) {
                    merge(err, LI_ERROR_TYPES?.NOT_CONNECTED);
                } else if (msg.includes('timeout')) {
                    merge(err, LI_ERROR_TYPES?.SEND_TIMEOUT);
                } else if (msg.includes('too_long')) {
                    merge(err, LI_ERROR_TYPES?.MSG_TOO_LONG);
                }
            }
        }
        return err;
    };

    const clearOldSendResults = () => {
        const sendResultsKeys = keys(messageSendResults);
        if (sendResultsKeys.length) {
            for (const key of sendResultsKeys) {
                delete messageSendResults[key];
            }
        }
    };

    const addToQueue = (messages, mode, cb) => {
        const recipientsLength = messages?.length;

        setSendingProgress((prevState) => ({
            current: queue.length ? (prevState?.current || 0) : 0,
            of: recipientsLength + (queue.length ? (prevState?.of || 0) : 0),
        }));

        if (!queue.length) {
            setLastResults(null);
        }
        if (!queue.length && queueTimes.length) {
            clearOldSendResults();
            queueTimes.length = 0;
        }

        let appendMethod = 'push';
        if (mode === CONTACT_MODES.SINGLE) {
            appendMethod = 'unshift';
        }

        const queueTime = (new Date()).getTime();
        queueTimes[appendMethod](queueTime);
        callbacks[appendMethod](cb);
        // eslint-disable-next-line no-restricted-syntax
        for (const msg of messages) {
            const { recipient } = msg;
            messageSendResults[queueTime + recipientsSeparator + recipient.talentId] = { queueTime, status: SENDING_STATUSES.PENDING };
            msg.queueTime = queueTime;
        }
        queue[appendMethod](...messages);
    };

    const updateProgress = () => {
        setSendingProgress((prevState) => ({
            current: (prevState?.current || 0) + 1,
            of: prevState?.of,
        }));

        const bulkResults = Object.values(messageSendResults)
            .filter((k) => queueTimes.includes(k.queueTime));

        const success = bulkResults.filter((r) => r.status === SENDING_STATUSES.SUCCESS);
        const fail = bulkResults.filter((r) => r.status === SENDING_STATUSES.FAIL);

        if (success.length + fail.length >= bulkResults.length) {
            setLastResults({
                success,
                fail,
                channel: 'linkedin',
            });
        }
    };

    const runQueue = (nested) => {
        setTimeout(async () => {
            if (isQueueRunning && !nested) {
                return;
            }

            if (!queue.length) {
                isQueueRunning = false;
                while (callbacks.length) {
                    callbacks.pop()(!callbacks.length, queueTimes.pop());
                }
                return;
            }
            isQueueRunning = true;
            const msg = queue.shift();
            const isInviteRequest = msg?.isInviteRequest;
            const { queueTime } = msg;
            const { talentId } = msg?.recipient || {};
            const sendResId = queueTime + recipientsSeparator + talentId;
            let error = null;

            // check candidate li connection status before sending to exclude double connection requests
            const candidateStatus = candidatesLinkedinStatuses[talentId];
            const isConnected = candidateStatus?.status === LINKEDIN_CONNECTION_STATUSES.CONNECTED;
            const isNotConnected = candidateStatus?.status === LINKEDIN_CONNECTION_STATUSES.NOT_CONNECTED;
            const isInvited = candidateStatus?.status === LINKEDIN_CONNECTION_STATUSES.INVITED;
            if (isInvited && !includes(msg?.message, '::EMULATE::')) {
                error = merge({}, LI_ERROR_TYPES.CONN_PENDING);
            } else if (isNotConnected && !isInviteRequest) {
                error = merge({}, LI_ERROR_TYPES.NOT_CONNECTED);
            } else if (isConnected && isInviteRequest) {
                error = merge({}, LI_ERROR_TYPES.DOUBLE_CONNECT);
            }
            const recipientUrl = get(msg.recipient, 'url.value', msg.recipient.url);

            if (!error && (!isInviteRequest || (isInviteRequest && userWeeklyInvitesLeft >= 0))) {
                messageSendResults[sendResId].status = SENDING_STATUSES.SENDING;

                const messageData = {
                    profileUrl: recipientUrl,
                    message: msg?.message,
                    authToken,
                    positionId: msg?.position?._id,
                    talentId,
                };

                const eventType = isInviteRequest
                    ? TRACKING_EVENTS.EMPLOYER_POSITION_CANDIDATES_SEND_LINKEDIN_INVITE_REQUEST
                    : TRACKING_EVENTS.EMPLOYER_POSITION_CANDIDATES_SEND_LINKEDIN_MESSAGE;

                const sendingFunction = async (data) => new Promise(
                    // eslint-disable-next-line no-promise-executor-return
                    (resolve) => {
                        const timeoutId = setTimeout(() => {
                            resolve({ error: 'timeout' });
                        }, LINKEDIN_SENDING_TIMEOUT);

                        const func = isInviteRequest
                            ? ChromeExtensionService.sendInvitation
                            : ChromeExtensionService.sendMessage;
                        func(data)
                            .then((result) => {
                                clearTimeout(timeoutId);
                                resolve(result);
                            })
                            .catch((err) => {
                                clearTimeout(timeoutId);
                                resolve({ error: isString(err?.code) ? err?.code : (toString(err) || '') });
                            });
                    },
                );

                trackEvent(eventType, {
                    positionId: msg?.position?._id,
                    positionName: msg?.position?.jobTitle,
                    employerId: msg?.position?.employerId,
                });

                try {
                    // eslint-disable-next-line no-await-in-loop
                    const emulation = window.location.href.includes('liEmulation');
                    const sentResult = emulation ? {} : await sendingFunction(messageData);
                    const messageUuid = emulation ? uuidv4() : get(sentResult, 'messageUuid') ?? get(sentResult, 'result.value.invitationId');
                    const isSuccessResult = emulation ? sample([true, false]) : !sentResult?.error && messageUuid;

                    if (isSuccessResult) {
                        ContactCandidateHttpService.addCandidateContactCampaignSentMessage({
                            sentMessage: {
                                ...msg?.selectedTemplate,
                                name: t(msg?.selectedTemplate?.name),
                                channel: CONTACT_CHANNEL_TYPES.LINKED_IN,
                                messageUuid: sentResult?.messageUuid,
                            },
                            positionId: msg?.position?._id,
                            talentId,
                        }).catch((err) => {
                            Logger.error(err);
                        });

                        if (msg?.recipient?.workflowId) {
                            await WorkflowHttpService.addWorkflowEvent(
                                msg?.recipient?.workflowId,
                                new WorkflowContactCandidateEvent(
                                    msg?.isInviteRequest,
                                    user?.impersonation?.realEntityRole === UserRoles?.employerRep ? EMPLOYER_ROLE : TALENT_EXPERT_ROLE,
                                    user?.impersonation?.realEntityId,
                                    msg?.employer,
                                ),
                            );
                        } else {
                            throw new Error('Required recipient.workflowId is not defined.');
                        }

                        messageSendResults[sendResId] = {
                            ...messageSendResults[sendResId],
                            status: SENDING_STATUSES.SUCCESS,
                            value: sentResult,
                            message: msg?.message,
                            position: msg?.position,
                            talentId,
                        };
                    } else {
                        error = emulation
                            ? sample([true, 'invalid profile', 'blocked', 'not exist', 'not connected'])
                            : (sentResult?.error || true);
                    }
                } catch (err) {
                    error = err;
                }
            }

            if (error) {
                const err = parseError(error);
                messageSendResults[sendResId] = {
                    ...messageSendResults[sendResId],
                    status: SENDING_STATUSES.FAIL,
                    error: err,
                    profileName: msg?.recipient?.name,
                    profileUrl: recipientUrl,
                };
                Logger.error(error);
            }

            updateProgress();

            sleep(queue.length ? random(linkedinMessageUserImitationDelayFrom, linkedinMessageUserImitationDelayTo) : 0).then(() => {
                runQueue(true);
            });
        }, 10);
    };

    const cleanLiSendingStates = () => {
        setIsDialogOpened(null);
        if (!isQueueRunning) {
            setLastResults(null);
            setSendingProgress(null);
        }
    };

    const afterSend = async (recipients, selectedTemplate, isLast, queueTime, isInviteRequest, mode, onSend, onClose) => {
        const userId = user?.origUserPrefs?._id;

        if (userId) {
            const result = await ChromeExtensionService.syncUserLinkedinConnections({ userId, authToken });
            fetchCandidatesContactData(true);
        }

        UserMessageTemplatesHttpService.addLastTemplateUsage(
            CONTACT_CHANNEL_TYPES.LINKED_IN,
            selectedTemplate?.name,
            getTemplateTypeByFlag(selectedTemplate),
            selectedTemplate.campaignId,
        ).catch((error) => {
            Logger.error(error);
        });

        const bulkResults = Object.values(messageSendResults)
            .filter((k) => queueTime === k.queueTime);
        const success = bulkResults.filter((r) => r.status === SENDING_STATUSES.SUCCESS);
        const fail = bulkResults.filter((r) => r.status === SENDING_STATUSES.FAIL);

        if (mode !== CONTACT_MODES.BULK) {
            if (success?.length) {
                const { position } = success[0];
                trackEvent(TRACKING_EVENTS.LINKEDIN_EDITOR_SEND_EMAIL, {
                    positionId: position._id,
                    positionName: position.jobTitle,
                    employerId: position.employerId,
                });
                trackTag([HOTJAR_TAGS.LINKEDIN_EDITOR_SEND_EMAIL]);
                showSuccessNotification(t('LINKEDIN_EDITOR_SENT_NOTIFICATION', {
                    defaultValue: 'Your LinkedIn message has been delivered. Stay tuned for recipient’s response.',
                }));
            } else if (fail?.length > 0) {
                const isStatusCode429 = !!fail.find(({ error }) => error.status === 429);

                if (isInviteRequest && isStatusCode429) {
                    showErrorNotification('CONTACT_CANDIDATE_LINKEDIN_FORM_NO_INVITES_LEFT_WARNING');
                    return;
                }
                showErrorNotification();
            }
            if (isFunction(onClose)) {
                onClose();
            }
        }
        if (isFunction(onSend)) {
            onSend({ success, fail, channel: 'linkedin' });
        }

        if (isLast) {
            countUserInvitesForWeek();
            setLastSendingId(0);
            stopSavingData(LOADING_AREAS.sendContactCandidateLinkedin);
            setIsDialogOpened((currentIsDialogOpened) => {
                if (!currentIsDialogOpened?.current) {
                    setLastResults(null);
                    setSendingProgress(null);
                }
                return currentIsDialogOpened;
            });
        }
    };

    const sendMessage = async (
        messageBody,
        selectedTemplate,
        recipients,
        position,
        employer,
        mode,
        onSend,
        onClose,
        sendingId,
    ) => {
        if (recipients?.length) {
            try {
                startSavingData(LOADING_AREAS.sendContactCandidateLinkedin);
                const recps = recipients.map((r) => ({ ...r }));
                const fstRecipient = recps[0];
                const candidateStatus = candidatesLinkedinStatuses[fstRecipient?.talentId];
                const isCandidateConnected = candidateStatus?.status === LINKEDIN_CONNECTION_STATUSES.CONNECTED;
                const isInviteRequest = !isCandidateConnected;

                const convertedBodySubjectForSystem = convertTextTemplateForSystem(messageBody);

                let recipientsLength = recps?.length;
                if (recipientsLength && isInviteRequest) {
                    if (userWeeklyInvitesLeft < recipientsLength) {
                        recipientsLength = userWeeklyInvitesLeft;
                        recps.length = recipientsLength;
                    }
                }

                if (recps?.length) {
                    const messages = recps.map((recipient, i) => ({
                        recipient,
                        isInviteRequest,
                        message: convertedBodySubjectForSystem,
                        selectedTemplate,
                        position,
                        employer,
                    }));
                    setLastSendingId(sendingId);

                    addToQueue(messages, mode, (isLast, queueTime) => {
                        afterSend(recps, selectedTemplate, isLast, queueTime, isInviteRequest, mode, onSend, onClose);
                    });
                    runQueue();
                }
            } catch (error) {
                Logger.error(error);
                showErrorNotification();
            }
        }
        return undefined;
    };

    const value = useMemo(() => ({
        lastResults,
        sendingProgress,
        setSendingProgress,
        sendMessage,
        resetSendingProgress: () => setSendingProgress(),
        setLastResults,
        isDialogOpened,
        setIsDialogOpened,
        queueLength: queue.length,
        cleanLiSendingStates,
        lastSendingId,
    }), [
        lastResults,
        sendingProgress,
        setSendingProgress,
        sendMessage,
        setLastResults,
        isDialogOpened,
        setIsDialogOpened,
        queue,
        cleanLiSendingStates,
        lastSendingId,
    ]);

    return <LinkedinMessagesSendingProvider
        value={value}
    >
        {children}
    </LinkedinMessagesSendingProvider>;
};

export default LinkedinMessagesSendingContext;
